import { useCallback, useEffect, useState } from 'react'
import { fetchInsights } from '../data/api'
import type { MatchInsights } from '../types'
import { SectionLabel } from './RoundTimeline'
import { fmtStamp } from '../data/fmt'
import { useCanCoach } from '../data/session'

const card = { borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)' } as const

/** How long to keep waiting for the pipeline's review before saying so.
 *
 *  Measured, the review lands 23-41 s after the scoreboard, so this is generous — but it has to be
 *  bounded. The reasons it might never arrive are real and silent: the month's AI budget is spent,
 *  or the Function App has lost its role on the borrowed Azure OpenAI account, which has happened.
 *  An unbounded spinner would present either as "still working". */
const WAIT_MS = 120_000
const POLL_MS = 4_000

export function AiReview({ id }: { id: string }) {
  // Regenerating spends AI budget and replaces what the team has already read — coaches only, and
  // the API enforces the same.
  const canCoach = useCanCoach()
  const [data, setData] = useState<MatchInsights | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  // The pipeline is writing it; we are waiting, not working. Distinct from `loading` because it is
  // the state a coach might want to stop waiting out and force.
  const [pending, setPending] = useState(false)
  const [gaveUp, setGaveUp] = useState(false)

  const load = useCallback(
    (refresh = false) => {
      setLoading(true)
      setErr(null)
      setGaveUp(false)
      return fetchInsights(id, refresh)
        .then((d) => {
          setData(d)
          setPending(d === null)
          return d
        })
        .catch((e) => {
          setErr((e as Error).message)
          return null
        })
        .finally(() => setLoading(false))
    },
    [id],
  )
  useEffect(() => void load(), [load])

  // Poll while the review is still being written. A GET costs a blob read and never generates, so
  // this is cheap — and it is the whole reason the page no longer races the queue for the same
  // review and pays for it twice.
  useEffect(() => {
    if (!pending) return
    const until = Date.now() + WAIT_MS
    const t = setInterval(async () => {
      if (Date.now() > until) {
        clearInterval(t)
        setPending(false)
        setGaveUp(true)
        return
      }
      const got = await fetchInsights(id).catch(() => null)
      if (got) {
        setData(got)
        setPending(false)
        clearInterval(t)
      }
    }, POLL_MS)
    return () => clearInterval(t)
  }, [pending, id])

  if (pending) {
    return (
      <div className="border border-border bg-surface p-8 text-center" style={card} aria-busy="true">
        <div className="font-display text-sm text-accent">Writing the match review…</div>
        <div className="mt-1 text-xs text-text-faint">
          It starts as soon as the parse finishes and usually takes under a minute. This page will
          fill in by itself.
        </div>
      </div>
    )
  }
  if (gaveUp && !data) {
    return (
      <div className="border border-border bg-surface p-6 text-center" style={card}>
        <div className="text-sm text-text-dim">No review has been written for this match.</div>
        <div className="mt-1 text-xs text-text-faint">
          It is normally written automatically when the demo is parsed. If this match is older than
          that, or the month’s AI budget ran out, it may need asking for.
        </div>
        {canCoach && (
          <button onClick={() => load(true)} className="mt-3 text-xs text-accent hover:underline">
            write it now
          </button>
        )}
      </div>
    )
  }
  if (loading && !data) {
    return (
      <div className="border border-border bg-surface p-8 text-center" style={card}>
        <div className="font-display text-sm text-accent">Analysing the match…</div>
        <div className="mt-1 text-xs text-text-faint">The AI coach is reading the scoreboard, duels, utility and economy. ~10s.</div>
      </div>
    )
  }
  if (err && !data) {
    return (
      <div className="border border-border bg-surface p-6" style={card}>
        <div className="text-sm text-loss">Couldn’t generate the review.</div>
        <div className="mt-1 text-xs text-text-faint">{err}</div>
        <button onClick={() => load(true)} className="mt-3 text-xs text-text-dim hover:text-accent">
          try again
        </button>
      </div>
    )
  }
  if (!data) return null

  return (
    <div className="space-y-5">
      {/* Summary */}
      <section className="border border-border bg-surface p-5" style={card}>
        <div className="flex items-center justify-between">
          <SectionLabel n="AI" label="Match review" />
          {canCoach && (
            <button
              onClick={() => load(true)}
              disabled={loading}
              className="text-xs text-text-dim hover:text-accent disabled:opacity-50"
            >
              {loading ? 'regenerating…' : 'regenerate'}
            </button>
          )}
        </div>
        <p className="mt-4 text-sm leading-relaxed text-text">{data.summary}</p>
      </section>

      {/* Strengths / Weaknesses / Improvements */}
      <div className="grid gap-4 md:grid-cols-3">
        <BulletCard label="Strengths" items={data.strengths} tone="var(--win)" />
        <BulletCard label="Weaknesses" items={data.weaknesses} tone="var(--loss)" />
        <BulletCard label="Focus next" items={data.improvements} tone="var(--accent)" />
      </div>

      {/* Per-player */}
      {data.players?.length > 0 && (
        <section className="border border-border bg-surface p-5" style={card}>
          <SectionLabel n="P" label="Per-player feedback" />
          {/* The API sends a player only their own row (PLAN §3) — say why, or a page showing one
              card out of five looks broken. */}
          {!canCoach && (
            <p className="mt-2 text-[11px] text-text-faint">
              Yours only. Teammates’ individual feedback stays between them and the coaches.
            </p>
          )}
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {data.players.map((p) => (
              <div
                key={p.steamid64}
                className="border border-border bg-surface-2 p-4"
                style={{ borderRadius: 'var(--radius)' }}
              >
                <div className="font-display text-sm uppercase tracking-wider text-text">{p.name}</div>
                <p className="mt-2 text-xs leading-relaxed text-text-dim">{p.assessment}</p>
                <p className="mt-2 text-xs leading-relaxed">
                  <span className="text-accent">Work on: </span>
                  <span className="text-text-dim">{p.improve}</span>
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      <p className="text-[11px] text-text-faint">
        Generated by {data.model ?? 'AI'}
        {data.generatedAt ? ` · ${fmtStamp(data.generatedAt)}` : ''} · cached until you regenerate.
        AI can misread — treat as a prompt for discussion, not gospel.
      </p>
      {data.stale && (
        // Saying so beats letting someone read a verdict built on numbers that have since changed.
        <p className="text-[11px] text-loss">
          This match has been re-parsed since the review was written, so it may describe numbers
          that have since been corrected. Hit <b>regenerate</b> for a current one.
        </p>
      )}
    </div>
  )
}

function BulletCard({ label, items, tone }: { label: string; items: string[]; tone: string }) {
  return (
    <section className="border border-border bg-surface p-5" style={card}>
      <div className="font-display text-xs uppercase tracking-wider text-text-dim">{label}</div>
      <ul className="mt-3 space-y-2">
        {(items ?? []).map((it, i) => (
          <li key={i} className="flex gap-2 text-xs leading-relaxed text-text">
            <span aria-hidden className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full" style={{ background: tone }} />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </section>
  )
}
